import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error' | 'info', text: string} | null>(null);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkToken = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const queryToken = queryParams.get('token');
        const queryType = queryParams.get('type');
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        if (queryToken && queryType === 'recovery') {
          const { data, error } = await supabase.auth.verifyOtp({ token_hash: queryToken, type: 'recovery' });
          if (error || !data.session) { setIsValidToken(false); setMessage({ type: 'error', text: 'The reset link has expired or has already been used.' }); return; }
          setIsValidToken(true); setMessage({ type: 'info', text: 'Please set your new password.' }); return;
        }
        if (hashType === 'recovery' && accessToken) {
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken || '' });
          if (sessionError || !sessionData.session) { setIsValidToken(false); setMessage({ type: 'error', text: 'The reset link has expired.' }); return; }
          setIsValidToken(true); setMessage({ type: 'info', text: 'Please set your new password.' }); return;
        }
        setIsValidToken(false); setMessage({ type: 'error', text: 'Invalid or expired reset link.' });
      } catch (error) { setIsValidToken(false); setMessage({ type: 'error', text: 'An error occurred while verifying the reset link.' }); }
    };
    checkToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) { setMessage({ type: 'error', text: 'Please enter a new password.' }); return; }
    if (password.length < 8) { setMessage({ type: 'error', text: 'Password must be at least 8 characters.' }); return; }
    if (password !== confirmPassword) { setMessage({ type: 'error', text: 'Passwords do not match.' }); return; }
    setIsLoading(true); setMessage(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) { setMessage({ type: 'error', text: 'An error occurred. Please try again.' }); }
      else { setIsPasswordReset(true); setMessage({ type: 'success', text: 'Your password has been changed!' }); setTimeout(() => navigate('/login', { replace: true }), 3000); }
    } catch (error) { setMessage({ type: 'error', text: 'An error occurred while changing your password.' }); }
    finally { setIsLoading(false); }
  };

  const strengthLevel = password.length >= 12 ? 3 : password.length >= 8 ? 2 : password.length >= 4 ? 1 : 0;
  const strengthColors = ['bg-neutral-200', 'bg-red-400', 'bg-amber-400', 'bg-green-400'];
  const strengthLabels = ['', 'Weak', 'Medium', 'Strong'];

  if (isValidToken === null) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-forest-200 border-t-forest-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-500 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 bg-gradient-to-br from-forest-400 via-forest-700 to-forest-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full border border-white/10" />
          <div className="absolute bottom-20 left-16 w-48 h-48 rounded-full bg-white/5" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <Link to="/" className="inline-flex items-baseline">
            <span className="font-display text-2xl font-bold tracking-[-0.04em] text-white">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              {isPasswordReset ? 'All Done!' : 'Set New\nPassword'}
            </h2>
            <p className="text-forest-100 max-w-sm leading-relaxed">
              {isPasswordReset ? 'Your password has been updated. You can now sign in with your new credentials.' : 'Choose a strong password to keep your account secure.'}
            </p>
          </div>
          <div className="space-y-2 text-sm text-forest-100">
            {['Use 8+ characters', 'Mix letters, numbers & symbols', 'Avoid common words'].map((tip) => (
              <div key={tip} className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-forest-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {tip}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12 bg-neutral-50">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-display text-2xl font-bold tracking-[-0.04em] text-forest-800">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
            </Link>
          </div>

          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
            isPasswordReset ? 'bg-green-100' : isValidToken ? 'bg-forest-100' : 'bg-red-100'
          }`}>
            {isPasswordReset ? (
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            ) : isValidToken ? (
              <svg className="w-6 h-6 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
            ) : (
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            )}
          </div>

          <h1 className="text-3xl font-display font-bold text-neutral-900 mb-1 tracking-tight">
            {isPasswordReset ? 'Password changed' : isValidToken ? 'Set new password' : 'Link expired'}
          </h1>
          <p className="text-neutral-500 mb-8">
            {isPasswordReset ? 'Redirecting to login...' : isValidToken ? 'Must be at least 8 characters' : 'This reset link is no longer valid'}
          </p>

          {/* Message */}
          {message && (
            <div className={`mb-6 p-3.5 rounded-lg text-sm flex items-start gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' :
              message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' :
              'bg-forest-50 text-forest-700 border border-forest-100'
            }`}>
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {message.text}
            </div>
          )}

          {isValidToken && !isPasswordReset ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">New password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isLoading}
                  className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-400/30 focus:border-forest-400 outline-none transition-all" placeholder="Enter new password" />
                {password && (
                  <div className="mt-2.5">
                    <div className="flex gap-1 mb-1">
                      {[0, 1, 2, 3].map(i => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < strengthLevel ? strengthColors[strengthLevel] : 'bg-neutral-100'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-neutral-400">{strengthLabels[strengthLevel]}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Confirm new password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={isLoading}
                  className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-forest-400/30 focus:border-forest-400 outline-none transition-all" placeholder="Re-enter password" />
              </div>
              <button type="submit" disabled={isLoading}
                className="w-full bg-forest-400 hover:bg-forest-500 disabled:bg-forest-400 text-white py-3 rounded-full font-semibold text-sm transition-all cursor-pointer flex items-center justify-center shadow-sm">
                {isLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Updating...</> : 'Reset Password'}
              </button>
            </form>
          ) : isPasswordReset ? (
            <Link to="/login" className="block w-full bg-forest-400 hover:bg-forest-500 text-white text-center py-3 rounded-full font-semibold text-sm transition-all shadow-sm">
              Go to Login
            </Link>
          ) : (
            <div className="space-y-3">
              <Link to="/find-password" className="block w-full bg-forest-400 hover:bg-forest-500 text-white text-center py-3 rounded-full font-semibold text-sm transition-all shadow-sm">
                Request New Link
              </Link>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
